// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Account → Passkeys (decision O4, owner 2026-10-06): the
 * account's passkeys, which confirm risky actions on own data (sharing,
 * deleting, adding a continuous source, roles). Add one (the browser's
 * own passkey prompt; a step-up for this very action first: the first
 * passkey with an emailed code), remove one (confirmed with a passkey),
 * or, for a lost passkey, ask for its removal with an emailed code: it
 * happens 72 hours later unless cancelled with a passkey (review M-2).
 * With passkeys, confirmations no longer come by email. Text only
 * (`myDataDom.ts`): names come from people.
 */

import { t } from '../i18n'
import { formatDate } from '../i18n/format'
import { cancelPasskeyRemoval, listPasskeys, passkeyOptions, registerPasskey, removePasskey, requestPasskeyRemoval, type PasskeyRemovalView, type PasskeyView } from '../services/ownDataApi'
import { createPasskey, passkeysSupported } from '../services/passkeyClient'
import { button, h, keepingDrafts } from './myDataDom'
import { askStepUp, guarded } from './myDataStepUp'
import { dialog } from './xrPlansUI'

const ERRORS: Record<string, Parameters<typeof t>[0]> = {
  NotAllowedError: 'passkeys.error.cancelled',
  InvalidStateError: 'passkeys.error.already',
  already_registered: 'passkeys.error.already',
  too_many_passkeys: 'passkeys.error.tooMany',
  wrong_passkey: 'passkeys.error.refused',
  code_expired: 'passkeys.error.expired',
  unauthorized: 'passkeys.error.signedOut',
  step_up_locked: 'myData.stepUp.locked',
  signed_out: 'passkeys.error.signedOut',
}
const errorText = (code: string): string => t(ERRORS[code] ?? 'passkeys.error.generic')

export function openPasskeys(trigger: HTMLElement | null = null): void {
  const d = dialog(t('passkeys.title'), trigger, 'xr-passkeys')
  const stepSlot = h('div', { class: 'mydata-stepup-slot' })
  let keys: PasskeyView[] | null = null
  let removals: PasskeyRemovalView[] = []
  let note: { text: string; error: boolean } | null = null
  let busy = false
  let resetName = false

  const load = async () => {
    const r = await listPasskeys()
    if (r.ok) {
      keys = r.value.passkeys
      removals = r.value.removals ?? []
    }
    else note = { text: errorText(r.error), error: true }
    render()
  }

  function render(): void {
    const reset = resetName ? ['passkey-name'] : []
    resetName = false
    keepingDrafts(d.body, draw, reset)
  }

  function draw(): void {
    const name = h('input', { type: 'text', id: 'passkey-name', class: 'mydata-input', maxlength: 60, autocomplete: 'off', placeholder: t('passkeys.namePlaceholder') })
    const supported = passkeysSupported()
    const list = h('ul', { class: 'mydata-share-list passkey-list', role: 'list' })
    for (const k of keys ?? []) {
      const pending = removals.find((r) => r.passkey === k.id)
      list.append(h('li', { class: 'mydata-share-row passkey-row', 'data-id': k.id },
        h('span', { class: 'mydata-row-title' }, k.name),
        h('span', { class: 'mydata-row-meta' }, [
          t('passkeys.added', { date: formatDate(new Date(k.createdAt), { dateStyle: 'medium' }) }),
          k.lastUsedAt ? t('passkeys.used', { date: formatDate(new Date(k.lastUsedAt), { dateStyle: 'medium' }) }) : t('passkeys.unused'),
          k.synced ? t('passkeys.synced') : '',
        ].filter(Boolean).join(' · ')),
        pending
          ? h('p', { class: 'mydata-notice is-error passkey-pending' }, t('passkeys.pending', { date: formatDate(new Date(pending.effectiveAt), { dateStyle: 'medium', timeStyle: 'short' }) }))
          : '',
        h('div', { class: 'mydata-actions' },
          button('pl-tab-btn pl-tab-btn-quiet passkey-remove', t('passkeys.remove'), { 'data-id': k.id, 'aria-label': t('passkeys.removeAria', { name: k.name }) }),
          pending
            ? button('pl-tab-btn pl-tab-btn-quiet passkey-keep', t('passkeys.keep'), { 'data-removal': pending.id })
            : button('pl-tab-btn pl-tab-btn-quiet passkey-lost', t('passkeys.lostButton'), { 'data-id': k.id, 'aria-label': t('passkeys.lostAria', { name: k.name }) }))))
    }
    d.body.replaceChildren(h('div', { class: 'mydata-tab passkeys-view' },
      stepSlot,
      h('p', { class: 'xr-plans-lead' }, t('passkeys.intro')),
      note ? h('p', { class: `mydata-notice${note.error ? ' is-error' : ''}`, role: note.error ? 'alert' : 'status' }, note.text) : '',
      keys === null ? h('p', { class: 'mydata-hint', role: 'status' }, t('myData.loading')) : keys.length ? list : h('p', { class: 'mydata-hint' }, t('passkeys.none')),
      supported
        ? h('div', { class: 'mydata-field' },
            h('label', { class: 'mydata-label', for: 'passkey-name' }, t('passkeys.nameLabel')),
            h('div', { class: 'mydata-pair' }, name, button('pl-tab-btn passkey-add', t('passkeys.add'))))
        : h('p', { class: 'mydata-notice is-error' }, t('passkeys.unsupported')),
      h('p', { class: 'mydata-hint' }, t('passkeys.lost'))))
  }

  d.body.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button')
    if (!btn || busy) return
    if (btn.classList.contains('passkey-add')) {
      const name = d.body.querySelector<HTMLInputElement>('#passkey-name')?.value ?? ''
      busy = true
      void (async () => {
        try {
          const opts = await guarded(stepSlot, 'passkey.add', () => passkeyOptions())
          if (!opts.ok) {
            note = opts.error === 'step_up_cancelled' ? null : { text: errorText(opts.error), error: true }
            return
          }
          const made = await createPasskey(opts.value.publicKey)
          if (!made.ok) {
            note = { text: errorText(made.error), error: true }
            return
          }
          const reg = await registerPasskey(opts.value.challenge, name, made.credential)
          note = reg.ok ? { text: t('passkeys.addedDone', { name: reg.value.passkey.name }), error: false } : { text: errorText(reg.error), error: true }
          resetName = reg.ok
        } finally {
          busy = false
          await load()
        }
      })()
      return
    }
    if (btn.classList.contains('passkey-lost') && btn.dataset.id) {
      // Lost passkey (review M-2): an emailed code asks for its removal, which happens only after 72 hours.
      const id = btn.dataset.id
      busy = true
      void (async () => {
        let r = await requestPasskeyRemoval(id)
        if (!r.ok && r.error === 'step_up_required') {
          r = (await askStepUp(stepSlot, 'passkey.remove', { lostPasskey: true })) ? await requestPasskeyRemoval(id) : { ok: false, status: 403, error: 'step_up_cancelled' }
        }
        busy = false
        if (r.ok) note = { text: t('passkeys.removalRequested', { date: formatDate(new Date(r.value.removal.effectiveAt), { dateStyle: 'medium', timeStyle: 'short' }) }), error: false }
        else if (r.error !== 'step_up_cancelled') note = { text: errorText(r.error), error: true }
        await load()
      })()
      return
    }
    if (btn.classList.contains('passkey-keep') && btn.dataset.removal) {
      const id = btn.dataset.removal
      busy = true
      void guarded(stepSlot, 'passkey.keep', () => cancelPasskeyRemoval(id)).then(async (r) => {
        busy = false
        if (r.ok) note = { text: t('passkeys.kept'), error: false }
        else if (r.error !== 'step_up_cancelled') note = { text: errorText(r.error), error: true }
        await load()
      })
      return
    }
    if (btn.classList.contains('passkey-remove') && btn.dataset.id) {
      const id = btn.dataset.id
      busy = true
      void guarded(stepSlot, 'passkey.remove', () => removePasskey(id)).then(async (r) => {
        busy = false
        if (r.ok) note = { text: t('passkeys.removed'), error: false }
        else if (r.error !== 'step_up_cancelled') note = { text: errorText(r.error), error: true }
        await load()
      })
    }
  })
  render()
  void load()
}
